/**
 * Tabs — bewusst ohne Radix, aus demselben Grund wie bei `ui/select.tsx`:
 * das WAI-ARIA-Muster fuer Tabs ist klein (Rollen, `aria-selected`, Pfeil-
 * tasten, Roving-Tabindex) und wir brauchen die Auswahl ohnehin **gesteuert**,
 * weil sie in der URL steht (`/account?tab=security`). Eine Bibliothek waere
 * hier vor allem ein zweiter Ort fuer denselben Zustand.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type TabItem = { value: string; label: string };

type TabListProps = {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  /** Beschriftung der Leiste fuer Screenreader. */
  label: string;
};

export function TabList({ items, value, onChange, label }: TabListProps) {
  function move(offset: number) {
    const index = items.findIndex((item) => item.value === value);
    if (index < 0) return;
    const next = items[(index + offset + items.length) % items.length];
    if (!next) return;
    onChange(next.value);
    document.getElementById(`tab-${next.value}`)?.focus();
  }

  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-1 border-b">
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            id={`tab-${item.value}`}
            aria-selected={active}
            aria-controls={`tabpanel-${item.value}`}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault();
                move(1);
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                move(-1);
              }
            }}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`tabpanel-${value}`} aria-labelledby={`tab-${value}`}>
      {children}
    </div>
  );
}
